import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';

import { AppConfigService } from '@core/appconfig.service';
import { ProductService } from '@core/product.service';
import { ApiResponse } from '@models/api-response';
import { KeyValuePair } from '@models/key-value-pair';
import {
  ILessonPlanCollection,
  ILessonPlannerResponse,
  ILessonPlanRequest,
  ILessonPlanResponse,
  LessonPlan
} from '@models/lesson-plan';
import {
  ILessonSection,
  ILessonSectionResponse,
  ILessonStep,
  ILessonStepCollection,
  ILessonTab
} from '@models/lesson-section';
import { IMaterialSearchResult } from '@models/material-search';
import { PagedResponse } from '@models/paged-response';
import { TopicGroup } from '@models/topic';
import { ITopicLesson, ITopicLessonResponse } from '@models/topic-lesson';
import { GradeType } from '@shared/enums/grade-type';
import { LessonSectionApiType, LessonSectionType, LessonStepType } from '@shared/enums/lesson-type';
import { MaterialFilterOptionTypes } from '@shared/enums/material-filter-option-types';
import { ProductType } from '@shared/enums/product-type';
import { VariantType } from '@shared/enums/variant-type';
import { Helpers } from '@shared/helpers';
import moment from 'moment';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

@Injectable()
export class LessonService {
  constructor(
    private http: HttpClient,
    private productService: ProductService,
    private appConfig: AppConfigService,
  ) { }

  getTopicLessons(productType: ProductType, variant: VariantType | string): Observable<ApiResponse<TopicGroup[]>> {
    return this.http.get<ApiResponse<TopicGroup[]>>(this.getTopicLessonsApiUrl(productType, variant))
      .pipe(
        map((res: ApiResponse<TopicGroup[]>) => new ApiResponse<TopicGroup[]>(true, {
          response: res.response.map((g: TopicGroup) => this.productService.mapTopicGroupValues(g)),
          messages: [...res.messages],
        })),
      );
  }

  getTopicLesson(
    productType: ProductType, grade: GradeType | string,
    topicKey: string = null
  ): Observable<ApiResponse<ITopicLesson>> {
    return this.http.get<ApiResponse<ITopicLessonResponse>>(this.getTopicLessonUrl(topicKey, productType, grade))
      .pipe(
        map(res => new ApiResponse<ITopicLesson>(true, {
          response: this.mapLessonStructure(res.response, productType),
          messages: res.messages,
        })),
      );
  }

  getTopicLessonPlans(
    productType: ProductType, gradeType: GradeType | string, topicKey: string = null,
    userId: string = null, lessonDate: string = null
  ): Observable<ApiResponse<ILessonPlanCollection>> {
    const params = [];
    if (topicKey) {
      params.push({ key: 'topicKey', value: topicKey });
    }
    if (lessonDate) {
      params.push({ key: 'lessonDate', value: lessonDate });
    }

    const url = this.getTopicLessonPlansUrl(productType, gradeType, userId, params);
    return this.http.get<ApiResponse<ILessonPlannerResponse>>(url)
      .pipe(
        map(res => new ApiResponse<ILessonPlanCollection>(true, {
          response: this.mapTopicLessonPlanCollection(res.response),
          messages: res.messages,
        })),
      );
  }

  updateTopicLessonPlan(request: ILessonPlanRequest): Observable<ApiResponse<ILessonPlanCollection>> {
    const { productType, gradeType } = request;
    const url = this.getTopicLessonPlansUrl(productType, gradeType);
    return this.http.patch<ApiResponse<ILessonPlannerResponse>>(url, request)
      .pipe(
        map(res => new ApiResponse<ILessonPlanCollection>(true, {
          response: this.mapTopicLessonPlanCollection(res.response),
          messages: res.messages,
        })),
      );
  }

  updateLessonNote(
    productType: ProductType, gradeType: GradeType | string, topicKey: string, note: string
  ): Observable<ApiResponse<boolean>> {
    const url = this.getTopicLessonUrl(topicKey, productType, gradeType);
    return this.http.patch(url, { note })
      .pipe(
        map(() => new ApiResponse<boolean>(true, { response: true, messages: [] })),
      );
  }

  mapTopicLessonPlanCollection(response: ILessonPlannerResponse): ILessonPlanCollection {
    return {
      ...response,
      lessons: response.lessons.map(plan => (new LessonPlan({
        ...plan,
        lessonDate: moment(plan.lessonDate, false).utc(),
        tabs: this.mapLessonPlanToSectionTabs(plan),
      }))),
    };
  }

  pinMaterial(
    productType: ProductType, gradeType: GradeType | string, componentId: string
  ): Observable<ApiResponse<boolean>> {
    const data = { productType, gradeType, componentId };
    return this.http.patch<ApiResponse<any>>(this.getMaterialUrl(), data)
      .pipe(
        map(res => new ApiResponse<boolean>(true, { response: true, messages: [...res.messages] })),
      );
  }

  unPinMaterial(
    productType: ProductType, gradeType: GradeType | string, componentId: string
  ): Observable<ApiResponse<boolean>> {
    const body = { productType, gradeType, componentId };
    return this.http.request<ApiResponse<any>>('delete', this.getMaterialUrl(), { body })
      .pipe(
        map(res => new ApiResponse<boolean>(true, { response: false, messages: [...res.messages] })),
      );
  }

  saveFilterOptions(
    productType: ProductType, gradeType: GradeType | string, filterOptions: string[]
  ): Observable<PagedResponse<any>> {
    const params: KeyValuePair[] = [
      { key: 'pageNumber', value: 1 },
      { key: 'pageSize', value: 25 },
      { key: 'productType', value: productType },
      { key: 'gradeType', value: gradeType },
    ];
    filterOptions.forEach(filterOption => params.push(
      { key: 'filterOptions', value: Helpers.getEnumKeyByEnumValue(MaterialFilterOptionTypes, filterOption) }
    ));
    const url = Helpers.buildUrlWithParameters(this.appConfig.apiUrl, '/digital-resources/superkids/material', params);
    return this.http.get<PagedResponse<IMaterialSearchResult[]>>(url)
      .pipe(
        map(res => new PagedResponse<IMaterialSearchResult[]>(true, {
          pageNumber: res.pageNumber,
          pageSize: res.pageSize,
          totalPages: res.totalPages,
          totalRecords: res.totalRecords,
          response: res.response,
          messages: [],
        })),
      );
  }

  private getMaterialUrl(): string {
    return `${this.appConfig.apiUrl}/digital-resources/superkids/material`;
  }

  private getTopicLessonsApiUrl(productType: ProductType, grade: GradeType | string): string {
    return `${this.appConfig.apiUrl}/digital-resources/superkids/lesson-navigation/product/${productType}/grade/${grade}`;
  }

  private getTopicLessonUrl(topicKey: string, productType: ProductType, grade: GradeType | string): string {
    const path = `/product/${productType}/grade/${grade}/topic`;
    const topicKeyPath = topicKey ? `/${topicKey}` : '';
    return `${this.appConfig.apiUrl}/digital-resources/superkids/lesson${path}${topicKeyPath}`;
  }

  private getTopicLessonPlansUrl(
    product: ProductType, grade: GradeType | string, userId: string = null, params: KeyValuePair[] = []
  ): string {
    const query = params
      .map((pair, index) => (`${index === 0 ? '?' : '&'}${pair.key}=${pair.value}`))
      .join('');
    const userSuffix = userId ? `/user/${userId}` : '';
    return `${this.appConfig.apiUrl}/digital-resources/superkids/lesson-plan/product/${product}/grade/${grade}${userSuffix}${query}`;
  }

  /** Maps the Api's lesson hierarchy into the hierarchy we need it in. */
  private mapLessonStructure(response: ITopicLessonResponse, productType: ProductType): ITopicLesson {
    // Find sub-sections that are listed as separate sections.
    const lessonBoxSubSections: ILessonSection[] = response.sections
      .filter(section => section.sectionType === LessonSectionApiType.LessonBox)
      .map(section => this.createLessonSectionFromApiSection(section));

    const tabs: ILessonTab[] = response.sections
      .filter(section => section.sectionType === LessonSectionApiType.Tab || section.subSectionName?.length > 0)
      .reduce((ret, section) => {
        const sectionToAdd = this.createLessonSectionFromApiSection(section);
        const tabIndex = ret.findIndex(t => t.name === section.name);

        // If the tab exists, we need to modify the tab's sections.
        if (tabIndex !== -1) {
          const newTabs = [...ret];

          if (sectionToAdd.type === LessonSectionType.Section) {
            newTabs[tabIndex].section = sectionToAdd;
          } else {
            newTabs[tabIndex].subSections = newTabs[tabIndex].subSections.concat(sectionToAdd);
          }
          return newTabs;
        }

        // Create a new tab.
        let mainSection: ILessonSection = null;
        const subSections: ILessonSection[] = [...lessonBoxSubSections];

        if (sectionToAdd.type === LessonSectionType.SubSection) {
          // This is a sub-section, not a section.
          subSections.push(sectionToAdd);
        } else {
          mainSection = sectionToAdd;
        }

        return ret.concat({ name: section.name, weight: section.sectionWeight, section: mainSection, subSections });
      }, []);

    // Sort the tabs.
    tabs.sort((a, b) => {
      if (a.weight < b.weight) {
        return -1;
      }
      return a.weight > b.weight ? 1 : 0;
    });

    return {
      topicKey: response.topicKey,
      topicName: response.topicName,
      topicWeight: response.topicWeight,
      topicType: response.topicType,
      unit: response.unit,
      unitName: response.unitName,
      unitWeight: response.unitWeight,
      includeInProduct: response.includeInProduct,
      includeInTrial: response.includeInTrial,
      gradeType: response.gradeType,
      note: response.note,
      productType,
      tabs,
      externalStandards: response.externalStandards,
    };
  }

  private createLessonSectionFromApiSection(section: ILessonSectionResponse): ILessonSection {
    const mappedSection: ILessonSection = {
      name: null,
      type: LessonSectionType.Section,
      weight: section.subSectionWeight,
      stepCollections: section.steps ? this.createLessonStepCollectionFromApiSteps(section.steps) : [],
    };

    if (section.subSectionName.length > 0) {
      mappedSection.name = section.subSectionName;
      mappedSection.type = LessonSectionType.SubSection;
    } else if (section.sectionType === LessonSectionApiType.LessonBox) {
      mappedSection.name = section.name;
      mappedSection.type = LessonSectionType.SubSection;
      mappedSection.weight = section.sectionWeight;
    } else if (section.sectionType === LessonSectionApiType.Tab) {
      mappedSection.name = section.title.trim();
    }

    return mappedSection;
  }

  private createLessonStepCollectionFromApiSteps(steps: ILessonStep[]): ILessonStepCollection[] {
    return steps.reduce((ret: ILessonStepCollection[], step: ILessonStep) => {
      const mappedStep = { ...step };

      // Lookup the last collection matching the same type by reversing the result. We need to
      // make an additional copy of the array before reversing as that method mutates the original
      // array as well.
      const index = [...ret].reverse().findIndex(list => list.type === mappedStep.type);
      if (index !== -1) {
        // Adds the mapped step to the result after calculating the index of the original result.
        const realIndex = ret.length - 1 - index;
        const copied = [...ret];
        copied[realIndex].steps = [...copied[realIndex].steps, mappedStep];
        return copied;
      }

      // Adds a new collection + mapped step to the result.
      return ret.concat({
        sectionId: mappedStep.sectionId,
        type: mappedStep.type,
        steps: [mappedStep],
        weight: mappedStep.weight,
      });
    }, []);
  }

  /** Maps the Api's lesson hierarchy into the hierarchy we need it in. */
  private mapLessonPlanToSectionTabs(response: ILessonPlanResponse): ILessonTab[] {
    const stepTypes = [LessonStepType.Step];
    const tabs: ILessonTab[] = response.sections.map((section) => {
      const sectionToAdd = this.createLessonSectionFromApiSection(section);
      sectionToAdd.stepCollections = sectionToAdd.stepCollections.filter(s => stepTypes.includes(s.type));
      return { name: section.name, section: sectionToAdd, subSections: [], weight: section.sectionWeight };
    });

    // Sort the tabs.
    tabs.sort((a, b) => {
      if (a.weight < b.weight) {
        return -1;
      }
      return a.weight > b.weight ? 1 : 0;
    });

    return tabs;
  }
}
